import { useCallback, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { useMutation } from "@tanstack/react-query";

import { useQuery } from "@tanstack/react-query";

import { api } from "../../lib/api";
import { queryKeys } from "../../lib/queryKeys";
import { Avatar } from "../ui/Avatar";
import { cn } from "../ui/cn";
import { PaperclipIcon } from "../ui/icons";
import { anchorStyle } from "./anchor";
import { asTyped } from "./as-typed";
import { FormatToolbar } from "./FormatToolbar";
import {
  CannedInlineMenu,
  CannedPicker,
  filterCanned,
  useCannedResponses,
} from "./CannedPicker";
import { EmojiPicker } from "./EmojiPicker";
import {
  AttachmentPreview,
  isImageType,
  type PreviewFile,
} from "../AttachmentPreview";
import { previewOfUpload } from "../../lib/uploads";
import { RichText, type RichTextHandle } from "./RichText";
import type { CannedResponse, User } from "../../types";
import { crmApi } from "../../lib/crm-api";
import type { CompanySearchResult } from "../../lib/crm-types";

/** What the owner's own controls are given to write into the surface. */
export interface EditorTools {
  /** Insert at the caret, or at the end if the editor never had one. */
  insertHtml: (html: string) => void;
}

export interface PendingUpload {
  storageKey: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
}

/** Caret text that opens an inline menu: the sigil and the word being typed. */
const CANNED_TRIGGER = /#([a-z0-9-]*)$/i;
const MENTION_TRIGGER = /@([a-z0-9'.\- ]{0,24})$/i;

/**
 * The whole writing surface: formatting toolbar, rich text, canned responses,
 * emoji, attachments and drag-and-drop.
 *
 * Extracted so replying and raising a new ticket use exactly the same editor.
 * They were briefly two components, and the new-ticket one immediately started
 * out worse -- no formatting, no snippets, no attachments. Anything that is
 * worth having when replying is worth having when starting a conversation, and
 * one component is the only way to keep that true as either changes.
 *
 * The HTML it produces is agent-authored and goes into an email, so it is
 * sanitised server-side on write. Nothing here is a security boundary.
 */
export function MessageEditor({
  initialHtml,
  onChange,
  uploads,
  onUploadsChange,
  placeholder,
  onError,
  onSubmit,
  allowAttachments = true,
  allowMentions = false,
  companyMentions = false,
  allowRichPaste = false,
  onFiles,
  onRichPaste,
  extraTools,
  surfaceHeight,
  surfaceStyle,
  footer,
}: {
  /**
   * Seeds the surface on mount. Used to restore an unsent draft.
   *
   * The editor is uncontrolled -- the contentEditable surface owns its own
   * content and reports out through onChange. There used to be an `html` prop
   * beside this one that read as the controlled half of that pair; it was
   * accepted, passed by both call sites, and never once read.
   */
  initialHtml?: string;
  onChange: (html: string) => void;
  uploads: PendingUpload[];
  onUploadsChange: (uploads: PendingUpload[]) => void;
  placeholder: string;
  onError: (message: string) => void;
  /** Cmd/Ctrl+Enter. Omitted where there is no single obvious send action. */
  onSubmit?: () => void;
  allowAttachments?: boolean;
  /** @mentions. Notes only -- there is nobody to notify in an outbound email. */
  allowMentions?: boolean;
  /**
   * @ mentions a CRM company instead of a colleague. Quick notes only: the
   * server files the lines that mention a company on its timeline. One or the
   * other, never both -- "@" cannot mean two things in one editor.
   */
  companyMentions?: boolean;
  /** Keep pasted HTML exactly as it came. Signatures only -- see RichText. */
  allowRichPaste?: boolean;
  /**
   * Take the files that are pasted or dropped, instead of attaching them.
   *
   * The signature editor's case: a signature has nothing to attach, but an
   * image dropped on it should still become part of it -- uploaded to the
   * shared library and inserted inline. It is handed the same insert as
   * `extraTools` so it can put the result where the caret was.
   */
  onFiles?: (files: File[], tools: EditorTools) => void;
  /** A formatted paste landed; the argument is the surface's new HTML. */
  onRichPaste?: (html: string) => void;
  /**
   * Extra toolbar controls, rendered after the built-in ones.
   *
   * How the signature editor adds its image picker without every reply
   * composer growing a button for something it has no use for.
   *
   * A function, not a node, because inserting has to go through the editor's
   * own handle. A control that instead appended to the `html` prop appeared to
   * work and lost its insert on the next keystroke: the surface is uncontrolled
   * and only reads that prop on mount, so the state and the DOM had diverged.
   */
  extraTools?: (tools: EditorTools) => React.ReactNode;
  /** How tall the writing surface is. See RichText's `surfaceHeight`. */
  surfaceHeight?: string;
  /** An exact height for a surface the agent has dragged. See RichText. */
  surfaceStyle?: CSSProperties;
  /**
   * Rendered inside the editor's box, beneath the writing surface.
   *
   * For controls that belong to the draft rather than to the app around it --
   * the signature toggle, today. A node rather than a function because,
   * unlike `extraTools`, nothing here needs to reach into the editor.
   */
  footer?: React.ReactNode;
}) {
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState<PreviewFile | null>(null);
  const [trigger, setTrigger] = useState<{
    kind: "canned" | "mention" | "company";
    query: string;
    top: number;
    left: number;
  } | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const editor = useRef<RichTextHandle>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const { data: canned } = useCannedResponses();
  const { data: agents } = useQuery({
    queryKey: queryKeys.users,
    queryFn: api.users,
    staleTime: 5 * 60_000,
    enabled: allowMentions,
  });

  const cannedMatches =
    trigger?.kind === "canned"
      ? filterCanned(canned ?? [], trigger.query, "body").slice(0, 6)
      : [];

  const mentionMatches =
    trigger?.kind === "mention" ? filterAgents(agents ?? [], trigger.query) : [];

  /* Companies are searched on the server -- there are thousands, where
     there are tens of colleagues. Nothing is asked until a letter is typed. */
  const companyQuery = trigger?.kind === "company" ? trigger.query.trim() : "";
  const { data: companyHits } = useQuery({
    queryKey: ["crm", "mention-search", companyQuery],
    queryFn: () => crmApi.search(companyQuery),
    enabled: companyQuery.length > 0,
    staleTime: 60_000,
    placeholderData: (previous) => previous,
  });
  const companyMatches =
    trigger?.kind === "company" && companyQuery
      ? (companyHits?.results ?? []).slice(0, 6)
      : [];

  const menuLength =
    trigger?.kind === "mention"
      ? mentionMatches.length
      : trigger?.kind === "company"
        ? companyMatches.length
        : cannedMatches.length;

  /** Re-evaluate the `#` and `@` triggers after every keystroke. */
  const syncTrigger = useCallback(() => {
    const before = editor.current?.textBeforeCaret() ?? "";

    const mention =
      allowMentions || companyMentions ? MENTION_TRIGGER.exec(before) : null;
    const cannedHit = CANNED_TRIGGER.exec(before);
    const match = mention ?? cannedHit;

    if (!match) {
      setTrigger(null);
      return;
    }

    const rect = editor.current?.caretRect();
    if (!rect) {
      setTrigger(null);
      return;
    }

    setActiveIndex(0);
    setTrigger({
      kind: mention ? (companyMentions ? "company" : "mention") : "canned",
      query: match[1] ?? "",
      // Above the caret, where the composers sit low on the screen -- and
      // below it when there is no room above, as on a notepad's first line.
      top: rect.top - 216 >= 8 ? rect.top - 216 : rect.bottom + 6,
      left: rect.left,
    });
  }, [allowMentions, companyMentions]);

  const insertCanned = useCallback(
    (response: CannedResponse, viaTrigger: boolean) => {
      if (viaTrigger && trigger) {
        // +1 for the "#" itself.
        editor.current?.replaceBeforeCaret(
          trigger.query.length + 1,
          asTyped(response.bodyHtml),
        );
      } else {
        editor.current?.insertHtml(asTyped(response.bodyHtml));
      }
      setTrigger(null);
    },
    [trigger],
  );

  /**
   * Insert a mention as markup, not text.
   *
   * The id is what actually notifies -- two agents can share a display name,
   * and names get edited. The visible "@Name" is only for reading.
   */
  const insertMention = useCallback(
    (agent: User) => {
      if (!trigger) return;
      editor.current?.replaceBeforeCaret(
        trigger.query.length + 1,
        `<span data-mention="${agent.id}">@${escapeText(agent.name)}</span>&nbsp;`,
      );
      setTrigger(null);
    },
    [trigger],
  );

  /** A company mention: the id files it, the name is for reading. */
  const insertCompany = useCallback(
    (company: CompanySearchResult) => {
      if (!trigger) return;
      editor.current?.replaceBeforeCaret(
        trigger.query.length + 1,
        `<span data-company="${company.id}">@${escapeText(company.name)}</span>&nbsp;`,
      );
      setTrigger(null);
    },
    [trigger],
  );

  const upload = useMutation({
    mutationFn: (file: File) => api.upload(file),
    onSuccess: (result) => onUploadsChange([...uploads, result]),
    onError: (cause: unknown) =>
      onError(cause instanceof Error ? cause.message : "Upload failed"),
  });

  async function acceptFiles(files: FileList | File[]): Promise<void> {
    for (const file of Array.from(files)) await upload.mutateAsync(file);
  }

  /** Writing into the surface, for the owner's own controls. */
  const tools: EditorTools = {
    insertHtml: (fragment) => editor.current?.insertHtml(fragment),
  };

  /**
   * What a dropped or pasted file does here.
   *
   * The owner's handler wins where there is one, so the signature editor can
   * turn a dropped logo into part of the signature rather than an attachment
   * it has no way to send.
   */
  const takeFiles = onFiles
    ? (files: FileList | File[]) => onFiles(Array.from(files), tools)
    : allowAttachments
      ? (files: FileList | File[]) => void acceptFiles(files)
      : undefined;

  function onKeyDown(event: React.KeyboardEvent): void {
    // The inline menu owns the arrow keys and Enter while it is open.
    if (trigger && menuLength > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex((index) => (index + 1) % menuLength);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((index) => (index - 1 + menuLength) % menuLength);
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        if (trigger.kind === "mention") {
          const agent = mentionMatches[activeIndex];
          if (agent) insertMention(agent);
        } else if (trigger.kind === "company") {
          const company = companyMatches[activeIndex];
          if (company) insertCompany(company);
        } else {
          const picked = cannedMatches[activeIndex];
          if (picked) insertCanned(picked, true);
        }
        return;
      }
      if (event.key === "Escape") {
        // Stopped so an enclosing dialog or ticket view does not also close.
        event.preventDefault();
        event.stopPropagation();
        setTrigger(null);
        return;
      }
    }

    if ((event.metaKey || event.ctrlKey) && event.key === "Enter" && onSubmit) {
      event.preventDefault();
      onSubmit();
    }
  }

  return (
    <div
      onDragOver={(event) => {
        // Only react to actual files; dragging selected text within the reply
        // must not turn the editor into a drop target.
        if (!takeFiles) return;
        if (!event.dataTransfer.types.includes("Files")) return;
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(event) => {
        const next = event.relatedTarget as Node | null;
        if (!next || !event.currentTarget.contains(next)) setDragging(false);
      }}
      onDrop={(event) => {
        if (!takeFiles) return;
        if (!event.dataTransfer.types.includes("Files")) return;
        event.preventDefault();
        setDragging(false);
        takeFiles(event.dataTransfer.files);
      }}
      className="relative"
    >
      {dragging ? (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-lg border-2 border-dashed border-brand-500 bg-brand-50/80 text-sm font-medium text-brand-700 dark:bg-brand-950/60 dark:text-brand-300">
          {onFiles ? "Drop to add an image" : "Drop to attach"}
        </div>
      ) : null}

      <div className="flex items-center gap-0.5 rounded-t-md border border-b-0 border-line bg-surface-2 px-1 py-1">
        <FormatToolbar editor={editor} onChange={onChange} />

        <EmojiPicker onPick={(emoji) => editor.current?.insertHtml(emoji)} />

        <CannedPicker onInsert={(response) => insertCanned(response, false)} />

        {allowAttachments ? (
          <>
            <input
              ref={fileInput}
              type="file"
              multiple
              className="hidden"
              onChange={(event) => {
                if (event.target.files) void acceptFiles(event.target.files);
                event.target.value = "";
              }}
            />
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={upload.isPending}
              title={
                upload.isPending
                  ? "Uploading…"
                  : "Attach a file, or drop one onto the editor"
              }
              aria-label="Attach a file"
              className={cn(
                "flex h-7 w-7 items-center justify-center rounded text-muted",
                "hover:bg-surface hover:text-brand-600 dark:hover:text-brand-400 disabled:opacity-50",
                upload.isPending && "animate-pulse",
              )}
            >
              <PaperclipIcon />
            </button>
          </>
        ) : null}

        {extraTools?.(tools)}
      </div>

      <div className="rounded-b-md border border-line">
        <RichText
          ref={editor}
          allowRichPaste={allowRichPaste}
          initialHtml={initialHtml}
          onInput={(next) => {
            onChange(next);
            syncTrigger();
          }}
          onKeyDown={onKeyDown}
          /*
           * A pasted screenshot becomes an attachment, exactly as if it had
           * been dropped on the editor. Not an inline image: that needs a
           * data: URL, which the sanitiser strips because one can carry an
           * SVG with script in it. The signature editor passes `onFiles` and
           * uploads to the shared library instead, which is the same reasoning
           * arriving at a real URL rather than at an attachment.
           */
          onPasteFiles={takeFiles}
          onRichPaste={onRichPaste}
          placeholder={placeholder}
          surfaceHeight={surfaceHeight}
          surfaceStyle={surfaceStyle}
        />

        {/*
          Inside the box, under the writing surface, sharing its border.

          This is where the signature control goes, and it is in here rather
          than below the editor because that is where it belongs: what it
          reveals is part of the message being written, not a panel about it.
          Rendered after RichText so it sits at the bottom of the draft, the
          way a sign-off does.
        */}
        {footer}
      </div>

      {trigger?.kind === "canned" ? (
        <CannedInlineMenu
          matches={cannedMatches}
          activeIndex={activeIndex}
          position={{ top: trigger.top, left: trigger.left }}
          onPick={(response) => insertCanned(response, true)}
        />
      ) : null}

      {trigger?.kind === "mention" && mentionMatches.length > 0 ? (
        <div
          role="listbox"
          aria-label="Mention someone"
          style={anchorStyle(trigger.top, trigger.left, 224, 208)}
          className="fixed z-40 overflow-hidden rounded-lg border border-line bg-surface shadow-lg"
        >
          <ul className="max-h-52 overflow-y-auto p-1">
            {mentionMatches.map((agent, index) => (
              <li key={agent.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  // onMouseDown, not onClick: clicking would blur the editor
                  // first and lose the caret the insert depends on.
                  onMouseDown={(event) => {
                    event.preventDefault();
                    insertMention(agent);
                  }}
                  className={cn(
                    "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left",
                    index === activeIndex ? "bg-surface-2" : "hover:bg-surface-2",
                  )}
                >
                  <Avatar
                    size="xs"
                    agent={{
                      id: agent.id,
                      name: agent.name,
                      color: agent.avatarColor,
                    }}
                  />
                  <span className="min-w-0 flex-1 truncate text-xs text-content">
                    {agent.name}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {trigger?.kind === "company" && companyMatches.length > 0 ? (
        <div
          role="listbox"
          aria-label="Mention a company"
          style={anchorStyle(trigger.top, trigger.left, 256, 208)}
          className="fixed z-40 overflow-hidden rounded-lg border border-line bg-surface shadow-lg"
        >
          <ul className="max-h-52 overflow-y-auto p-1">
            {companyMatches.map((company, index) => (
              <li key={company.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    insertCompany(company);
                  }}
                  className={cn(
                    "flex w-full flex-col rounded px-2 py-1.5 text-left",
                    index === activeIndex ? "bg-surface-2" : "hover:bg-surface-2",
                  )}
                >
                  <span className="truncate text-xs text-content">{company.name}</span>
                  {company.city || company.domain ? (
                    <span className="truncate text-[11px] text-faint">
                      {[company.city, company.domain].filter(Boolean).join(" · ")}
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {uploads.length ? (
        <ul className="mt-2 flex flex-wrap gap-2">
          {uploads.map((file) => (
            <PendingChip
              key={file.storageKey}
              file={file}
              onPreview={setPreview}
              onRemove={() =>
                onUploadsChange(
                  uploads.filter((entry) => entry.storageKey !== file.storageKey),
                )
              }
            />
          ))}
        </ul>
      ) : null}

      {preview ? (
        <AttachmentPreview file={preview} onClose={() => setPreview(null)} />
      ) : null}
    </div>
  );
}

/**
 * One file waiting to be sent.
 *
 * A picture shows itself. That is the point of the change: an attachment strip
 * that reads "Screenshot 2026-08-28 at 14.32.23.png" tells you a screenshot is
 * attached and nothing at all about *which* screenshot, which is the only thing
 * worth checking before it goes to a customer.
 *
 * Clicking opens the viewer, which was impossible until uploads had an address
 * of their own -- the file existed in storage and nothing could name it, so the
 * only way to see what was about to be sent was to send it. Removing stays a
 * separate control rather than the whole chip being a toggle: they are opposite
 * intentions and the destructive one should not be the easy miss.
 */
function PendingChip({
  file,
  onPreview,
  onRemove,
}: {
  file: PendingUpload;
  onPreview: (preview: PreviewFile) => void;
  onRemove: () => void;
}) {
  const preview = previewOfUpload(file);
  const isPicture = preview?.inlineUrl !== null && isImageType(file.mimeType);

  return (
    <li className="relative">
      <button
        type="button"
        disabled={!preview}
        onClick={() => preview && onPreview(preview)}
        title={`${file.filename} (${file.mimeType})`}
        className={cn(
          "block overflow-hidden rounded border border-line bg-surface-2 text-xs text-content",
          preview ? "hover:border-brand-500" : "cursor-default",
        )}
      >
        {isPicture && preview?.inlineUrl ? (
          <img
            src={preview.inlineUrl}
            alt={file.filename}
            className="max-h-24 max-w-[10rem] object-contain"
          />
        ) : null}
        <span className="block max-w-[10rem] truncate px-2 py-1 text-left">
          {isPicture ? null : <span aria-hidden>📎 </span>}
          {file.filename}
        </span>
      </button>

      <button
        type="button"
        onClick={onRemove}
        className="absolute -right-1.5 -top-1.5 flex size-4 items-center justify-center rounded-full border border-line bg-surface text-faint hover:text-red-500"
        aria-label={`Remove ${file.filename}`}
      >
        ×
      </button>
    </li>
  );
}

/** Clears the surface after a successful send. */
export function useEditorReset(): {
  key: number;
  reset: () => void;
} {
  const [key, setKey] = useState(0);
  return { key, reset: () => setKey((value) => value + 1) };
}





/** Agents whose name starts with, or contains, what has been typed. */
function filterAgents(agents: User[], query: string): User[] {
  const needle = query.trim().toLowerCase();
  const ranked = agents
    .map((agent) => {
      const name = agent.name.toLowerCase();
      if (!needle) return { agent, score: 0 };
      if (name.startsWith(needle)) return { agent, score: 0 };
      // Match any word, so "@ru" finds "Jade Ruysseveldt".
      if (name.split(/\s+/).some((word) => word.startsWith(needle))) {
        return { agent, score: 1 };
      }
      if (name.includes(needle)) return { agent, score: 2 };
      return { agent, score: -1 };
    })
    .filter((row) => row.score >= 0)
    .sort((a, b) => a.score - b.score);

  return ranked.slice(0, 6).map((row) => row.agent);
}

/** Escapes a name so one containing "<" cannot inject markup. */
function escapeText(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Visible text of a draft, for "is there anything to send". */
export function editorText(html: string): string {
  const host = document.createElement("div");
  host.innerHTML = html;
  return (host.textContent ?? "").trim();
}
